import { useNavigate, useOutletContext, Navigate } from 'react-router-dom'
import { useLicense } from '../contexts/LicenseContext'
import type { LicenseFeature } from '@shared/license-types'

interface LayoutContext {
  isDemoActive: boolean
}

const modes = [
  {
    key: 'outside-in',
    emoji: '🤖',
    title: 'Outside-In',
    subtitle: 'Talent Navigator Door Buster',
    description:
      'No client data needed. Enter the company name, industry, and headcount — Talent Navigator researches public data and applies the TSCV methodology to generate a credible transformation navigator in minutes.',
    tags: ['Generated', 'Claude or GPT-4o', 'Public Data', '~10 min'],
    gradient: 'from-purple-600 to-purple-800',
    border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-700',
  },
  {
    key: 'inside-out',
    emoji: '📂',
    title: 'Inside-Out',
    subtitle: 'Client Data First',
    description:
      'Upload your TSCV output files, org charts, or task inventories. The app maps client data directly to the navigator schema — Talent Navigator fills any gaps — producing the most accurate and customized output.',
    tags: ['Excel / CSV / JSON', 'Real Client Data', 'Smart Gap-Fill', 'Most Accurate'],
    gradient: 'from-blue-600 to-blue-800',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
  },
]

export default function ModeSelect() {
  const navigate = useNavigate()
  const { isDemoActive } = useOutletContext<LayoutContext>()
  const { status: licenseStatus } = useLicense()

  if (isDemoActive) return <Navigate to="/" replace />

  const hasFeature = (feature: string): boolean => {
    if (licenseStatus?.licenseType !== 'trial') return true
    return licenseStatus.license?.features.includes(feature as LicenseFeature) ?? true
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-full p-8 animate-fade-in">
      <div className="max-w-3xl w-full">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-semibold text-gray-900 mb-3">
            How would you like to build this navigator?
          </h1>
          <p className="text-gray-500 text-base">
            Choose your starting point. You can always enrich an AI-generated navigator with real data later.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {modes.map((mode) => {
            const allowed = hasFeature(mode.key)
            return (
              <button
                key={mode.key}
                onClick={() => allowed && navigate(`/${mode.key}`)}
                disabled={!allowed}
                className={`text-left bg-white rounded-2xl p-6 border-2 ${mode.border} transition-all focus:outline-none focus:ring-2 focus:ring-purple-300 ${allowed ? 'card-hover cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
              >
                {/* Header */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mode.gradient} flex items-center justify-center text-2xl mb-4`}>
                  {mode.emoji}
                </div>

                <div className="mb-1">
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                    {mode.subtitle}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{mode.title}</h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{mode.description}</p>

                <div className="flex flex-wrap gap-2">
                  {mode.tags.map((tag) => (
                    <span key={tag} className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${mode.badge}`}>
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-5 flex items-center text-sm font-semibold text-gray-900">
                  {allowed ? (
                    <>Get started <span className="ml-2 text-base">→</span></>
                  ) : (
                    <span className="text-gray-400 font-normal">Not included in your license</span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
